import { ServiceClient, ServiceError, TokenFunc } from "@securityjourney/service-client";
import {
  components as contentComponents,
  paths,
} from "@securityjourney/svc-content/src/gen/content_api";

import {
  components as catalogComponents,
  paths as catalogPaths,
} from "@securityjourney/svc-content/src/gen/catalog";

// Base tab properties common to all tab types
type BaseSandboxTab = {
  title: string;
  iconClass?: string;
};

// Individual SandboxTab variants
export type AppTab = BaseSandboxTab & {
  app: contentComponents["schemas"]["securityjourney.proto.content.LessonInfoV2.Sandbox.Tab.App"];
  type: "app";
};

export type EditorTab = BaseSandboxTab & {
  editor: contentComponents["schemas"]["securityjourney.proto.content.LessonInfoV2.Sandbox.Tab.Editor"];
  type: "editor";
};

export type InterceptTab = BaseSandboxTab & {
  intercept: contentComponents["schemas"]["securityjourney.proto.content.LessonInfoV2.Sandbox.Tab.Intercept"];
  type: "intercept";
};

export type TestsTab = BaseSandboxTab & {
  tests: contentComponents["schemas"]["securityjourney.proto.content.TestsConfiguration"];
  type: "tests";
};

export type UnknownTab = BaseSandboxTab & {
  type: "unknown";
};

// Union type of all possible tab variants
export type SandboxTab = AppTab | EditorTab | InterceptTab | TestsTab | UnknownTab;

export type LessonDetails = Omit<
  contentComponents["schemas"]["securityjourney.proto.content.LessonInfoV2"],
  "sandbox"
> & {
  completionType: "tests" | "flagSubmission" | "steps";
  hasCodeTests: boolean;
  isCtf: boolean;
  hasFinishButton: boolean;
  engines: string[];
  tests: contentComponents["schemas"]["securityjourney.proto.content.TestsConfiguration"] | null;
  sandbox: Omit<
    contentComponents["schemas"]["securityjourney.proto.content.LessonInfoV2.Sandbox"],
    "tabs"
  > & {
    tabs: SandboxTab[];
  };
  since?: string;
};
export type LessonInstructions =
  contentComponents["schemas"]["securityjourney.proto.content.Instructions"];
export type HintDetails = contentComponents["schemas"]["securityjourney.proto.content.Hint"];
export type StepDetails =
  contentComponents["schemas"]["securityjourney.proto.content.Instructions.Step"];
export type TestConfiguration =
  contentComponents["schemas"]["securityjourney.proto.content.TestsConfiguration"];
export type TestCase = contentComponents["schemas"]["securityjourney.proto.content.TestCase"];
export type ReplParam = {
  "repl-engine": string;
};

type CatalogParams = {
  name?: string;
  description?: string;
  tags?: string[];
  lessonTypes?: catalogComponents["schemas"]["securityjourney.proto.content.LessonConfigurationType"][];
  recentlyReleased?: boolean;
  recentlyRefreshed?: boolean;
  levelId?: number;
  type?: catalogComponents["schemas"]["securityjourney.proto.content.LessonConfigurationType"];
  hasTests?: boolean;
};

export class LessonContentClient extends ServiceClient<paths & catalogPaths> {
  constructor(getToken: TokenFunc, baseUrl?: string) {
    super({
      getToken,
      url: baseUrl,
    });
  }

  getLessonDetails(lessonKey: string): Promise<LessonDetails> {
    const func = this.fetcher
      .path("/svc/content/lesson/{lessonKey}/v/{lessonVersion}")
      .method("get")
      .create();
    const args = {
      lessonKey,
      lessonVersion: 1,
    };

    return this.withAuthorization({})
      .then(init => func(args, init))
      .then(response => {
        const details = response.data;

        if (!details) {
          throw new ServiceError(new Error("No lesson details returned"));
        }

        // Pull the choices from the repl-engine parameter and put it at the top level of the lesson details
        const replEngineParam = details.sandbox?.parameters?.find(p => p.key === "repl-engine");
        const choices = replEngineParam?.choices;

        // Pull the tests cases from the test tab if present
        const testTab = details.sandbox?.tabs.find(t => "tests" in t && t.tests);
        const tests = testTab && "tests" in testTab ? (testTab.tests ?? null) : null;

        // Add type property to each tab based on its content
        const tabs: SandboxTab[] =
          details.sandbox?.tabs?.map(tab => {
            if ("app" in tab && tab.app) {
              return {
                ...tab,
                app: tab.app,
                type: "app",
              } as AppTab;
            } else if ("intercept" in tab && tab.intercept) {
              return {
                ...tab,
                intercept: tab.intercept,
                type: "intercept",
              } as InterceptTab;
            } else if ("editor" in tab && tab.editor) {
              return {
                ...tab,
                editor: tab.editor,
                type: "editor",
              } as EditorTab;
            } else if ("tests" in tab && tab.tests) {
              return {
                ...tab,
                tests: tab.tests,
                type: "tests",
              } as TestsTab;
            } else {
              return {
                ...tab,
                type: "unknown",
              } as UnknownTab;
            }
          }) || [];

        // Pull out some properties of the lesson sandbox for easier access
        const hasCodeTests = !!tests;
        const isCtf = details.sandbox?.instructions.requiresFlagSubmission || false;

        // If this sandbox doesn't have tests and isn't CTF, then we'll need a "finish" button
        const hasFinishButton = !hasCodeTests && !isCtf;

        // Determine completion type
        const completionType: LessonDetails["completionType"] = hasCodeTests
          ? "tests"
          : isCtf
            ? "flagSubmission"
            : "steps";

        // Return the lesson details with added engines, tests, and typed tabs
        const returnDetails: LessonDetails = {
          ...response.data,
          completionType,
          hasCodeTests,
          isCtf,
          hasFinishButton,
          sandbox: {
            ...response.data.sandbox!,
            tabs,
          },
          engines: choices ?? [],
          tests,
        };
        return returnDetails;
      })
      .catch(ServiceError.raise);
  }

  getTags(): Promise<Record<string, string[]>> {
    const func = this.fetcher.path("/svc/content/catalog/tags").method("get").create();

    return this.withAuthorization({})
      .then(init => func({}, init))
      .then(response => {
        if (!response.data?.tags) {
          throw new ServiceError(new Error("No tags returned"));
        }

        return Object.fromEntries(
          Object.entries(response.data.tags).map(([key, value]) => [key, value?.values ?? []])
        );
      })
      .catch(error => {
        return ServiceError.raise(error);
      });
  }

  getCollections(
    params: CatalogParams = {}
  ): Promise<catalogComponents["schemas"]["securityjourney.proto.content.Collection"][]> {
    const func = this.fetcher.path("/svc/content/catalog/collections").method("get").create();

    return this.withAuthorization({})
      .then(init => func(params, init))
      .then(response => {
        if (!response.data?.collections) {
          throw new ServiceError(new Error("No collections returned"));
        }
        return response.data.collections;
      })
      .catch(error => {
        return ServiceError.raise(error);
      });
  }

  getLessons(
    params: CatalogParams = {}
  ): Promise<catalogComponents["schemas"]["securityjourney.proto.content.Lesson"][]> {
    const func = this.fetcher.path("/svc/content/catalog/lessons").method("get").create();

    return this.withAuthorization({})
      .then(init => func(params, init))
      .then(response => {
        if (!response.data?.lessons) {
          throw new ServiceError(new Error("No lessons returned"));
        }
        return response.data.lessons;
      })
      .catch(error => {
        return ServiceError.raise(error);
      });
  }
}
